import { useEffect, useState } from "react";
import { api } from "@/lib/api-client";
import "./BatchCheckInModal.css";

interface CheckInResult {
  releasedLockCount: number;
  unfinishedDocCount: number;
  audit: unknown;
}

interface Props {
  batchId: string;
  open: boolean;
  onClose: () => void;
  onSuccess: (result: CheckInResult) => void;
}

type Reason = "" | "end_of_shift" | "need_help" | "blocked" | "other";

const REASONS: ReadonlyArray<{ value: Reason; label: string }> = [
  { value: "", label: "— no reason —" },
  { value: "end_of_shift", label: "End of shift" },
  { value: "need_help", label: "Need help" },
  { value: "blocked", label: "Blocked" },
  { value: "other", label: "Other" },
];

export function BatchCheckInModal({ batchId, open, onClose, onSuccess }: Props) {
  const [reason, setReason] = useState<Reason>("");
  const [note, setNote] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Reset form state whenever the modal opens (mirrors UploadDialog convention).
  useEffect(() => {
    if (open) {
      setReason("");
      setNote("");
      setError(null);
      setSubmitting(false);
    }
  }, [open]);

  if (!open) return null;

  async function handleSubmit() {
    setSubmitting(true);
    setError(null);
    try {
      const body: { reason?: Reason; note?: string } = {};
      if (reason) body.reason = reason;
      if (note.trim()) body.note = note.trim();
      const result = await api.post<CheckInResult>(
        `/batches/${batchId}/checkin`,
        body,
      );
      onSuccess(result);
    } catch (e) {
      setError(
        e instanceof Error ? `Check-in failed: ${e.message}` : "Check-in failed",
      );
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="batch-checkin-overlay"
      onClick={(e) => {
        if (e.target === e.currentTarget && !submitting) onClose();
      }}
    >
      <div
        className="batch-checkin-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Check in batch"
      >
        <div className="batch-checkin-modal__header">
          <h3 className="batch-checkin-modal__title">Check In Batch</h3>
        </div>

        <div className="batch-checkin-modal__body">
          <p className="batch-checkin-modal__description">
            Release this batch back to the queue. Any completed document progress
            is preserved.
          </p>

          <div className="batch-checkin-modal__field">
            <label
              className="batch-checkin-modal__label"
              htmlFor="batch-checkin-reason"
            >
              Reason
            </label>
            <select
              id="batch-checkin-reason"
              className="batch-checkin-modal__select"
              value={reason}
              onChange={(e) => setReason(e.target.value as Reason)}
              disabled={submitting}
            >
              {REASONS.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>

          <div className="batch-checkin-modal__field">
            <label
              className="batch-checkin-modal__label"
              htmlFor="batch-checkin-note"
            >
              Note
            </label>
            <textarea
              id="batch-checkin-note"
              className="batch-checkin-modal__textarea"
              maxLength={500}
              rows={4}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder="Optional — visible to supervisors in the audit trail"
              disabled={submitting}
            />
          </div>

          {error && <div className="batch-checkin-modal__error">{error}</div>}
        </div>

        <div className="batch-checkin-modal__actions">
          <button
            type="button"
            className="batch-checkin-modal__btn-cancel"
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            className="batch-checkin-modal__btn-submit"
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Checking In..." : "Check In"}
          </button>
        </div>
      </div>
    </div>
  );
}
